import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/ui/PageHeader';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import EmptyState from '../components/ui/EmptyState';
import Alert from '../components/ui/Alert';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import Pagination from '../components/ui/Pagination';

const PAGE_SIZE = 12;

// SVG Icons
const BuildingIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
    />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

const MapPinIcon = () => (
  <svg
    className="w-3.5 h-3.5 flex-shrink-0 text-slate-400"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
    />
  </svg>
);

const UsersIcon = () => (
  <svg
    className="w-4 h-4 text-slate-400"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
    />
  </svg>
);

const BookIcon = () => (
  <svg
    className="w-4 h-4 text-slate-400"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
    />
  </svg>
);

const UserIcon = () => (
  <svg
    className="w-4 h-4 text-slate-400"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
    />
  </svg>
);

const EyeIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
    />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const ClipboardIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z"
    />
  </svg>
);

const DocumentIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
    />
  </svg>
);

const PauseIcon = () => (
  <svg
    className="w-3.5 h-3.5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25v13.5m-7.5-13.5v13.5" />
  </svg>
);

const TrashIcon = () => (
  <svg
    className="w-3.5 h-3.5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
  </svg>
);

const SearchIcon = () => (
  <svg
    className="w-4 h-4 text-slate-400"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
    />
  </svg>
);

const Companies = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [actionError, setActionError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchCompanies();
  }, []);
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const res = await api.get('/companies');
      // Filtrăm doar firmele CLIENT (nu SSM)
      setCompanies((res.data || []).filter((c) => c.type === 'CLIENT'));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = (company) => {
    setDeactivateTarget(company);
    setActionError('');
  };
  const handleDelete = (company) => {
    setDeleteTarget(company);
    setDeleteConfirmName('');
    setActionError('');
  };

  const confirmDeactivate = async () => {
    if (!deactivateTarget) return;
    setActionLoading(deactivateTarget.id + '_deactivate');
    setActionError('');
    try {
      await api.patch(`/companies/${deactivateTarget.id}/deactivate`);
      setCompanies((prev) => prev.filter((c) => c.id !== deactivateTarget.id));
      setDeactivateTarget(null);
    } catch (err) {
      setActionError(err.response?.data?.error || t('companies.deactivateErrorMsg'));
    } finally {
      setActionLoading(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteConfirmName !== deleteTarget.name) {
      setActionError(t('companies.nameMismatch'));
      return;
    }
    setActionLoading(deleteTarget.id + '_delete');
    setActionError('');
    try {
      await api.delete(`/companies/${deleteTarget.id}`);
      setCompanies((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
      setDeleteConfirmName('');
    } catch (err) {
      setActionError(
        err.response?.data?.error || err.response?.data?.details || t('companies.deleteErrorMsg')
      );
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = companies.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.cui?.toLowerCase().includes(search.toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  if (loading) return <Spinner size="lg" label={t('common.loading')} />;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('companies.clientCompaniesTitle')}
        subtitle={t('companies.registeredCount', { count: companies.length })}
        actions={
          ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN'].includes(user?.role) && (
            <Button variant="primary" size="md" onClick={() => navigate('/companies/new')}>
              <PlusIcon />
              {t('companies.newCompany')}
            </Button>
          )
        }
      />

      {/* Căutare */}
      <Card>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <SearchIcon />
          </div>
          <input
            type="text"
            placeholder={t('companies.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10"
          />
        </div>
      </Card>

      {filtered.length === 0 ? (
        <EmptyState
          icon={<BuildingIcon />}
          title={companies.length === 0 ? t('companies.noClient') : t('companies.noMatches')}
          description={companies.length === 0 ? t('companies.createHint') : ''}
          actionLabel={
            companies.length === 0 && ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN'].includes(user?.role)
              ? t('companies.newCompany')
              : undefined
          }
          onAction={
            companies.length === 0 && ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN'].includes(user?.role)
              ? () => navigate('/companies/new')
              : undefined
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {paged.map((company) => {
              const isDeactivating = actionLoading === company.id + '_deactivate';
              const isDeleting = actionLoading === company.id + '_delete';
              const isAnyAction = isDeactivating || isDeleting;

              return (
                <Card
                  key={company.id}
                  hover
                  className={`flex flex-col ${!company.active ? 'opacity-60' : ''}`}
                >
                  {/* Header card */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-base truncate">
                        {company.name}
                      </h3>
                      <p className="text-xs text-slate-400 dark:text-slate-400 mt-0.5">
                        CUI: {company.cui}
                      </p>
                    </div>
                    <Badge color={company.active ? 'green' : 'red'} className="flex-shrink-0 ml-2">
                      {company.active ? t('common.status.active') : t('common.status.inactive')}
                    </Badge>
                  </div>

                  {/* Servicii oferite */}
                  <div className="flex items-center gap-1.5 mb-3 flex-wrap">
                    {company.offersSSM && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 dark:bg-slate-800/60 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-slate-700">
                        SSM
                      </span>
                    )}
                    {company.offersSU && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-50 dark:bg-slate-800/60 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-slate-700">
                        SU
                      </span>
                    )}
                    {!company.offersSSM && !company.offersSU && (
                      <span className="text-[10px] text-slate-400 dark:text-slate-400 italic">
                        {t('companies.noServices')}
                      </span>
                    )}
                  </div>

                  {company.address && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-3 flex items-center gap-1.5">
                      <MapPinIcon />
                      <span className="truncate">{company.address}</span>
                    </p>
                  )}

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                    {[
                      {
                        icon: <UsersIcon />,
                        val: company._count?.employees || 0,
                        label: t('companies.employees'),
                      },
                      {
                        icon: <BookIcon />,
                        val: company._count?.assignments || 0,
                        label: t('companies.courses'),
                      },
                      {
                        icon: <UserIcon />,
                        val: company._count?.users || 0,
                        label: t('companies.users'),
                      },
                    ].map((s) => (
                      <div key={s.label} className="bg-surface-secondary rounded-lg py-2 px-1">
                        <div className="flex items-center justify-center mb-0.5">{s.icon}</div>
                        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 tabular-nums">
                          {s.val}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">{s.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Acțiuni principale */}
                  <div className="space-y-2 mt-auto">
                    <Button
                      variant="primary"
                      size="sm"
                      className="w-full"
                      onClick={() => navigate(`/companies/${company.id}`)}
                    >
                      <EyeIcon />
                      {t('companies.companyDetails')}
                    </Button>

                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs"
                        onClick={() => navigate(`/companies/${company.id}/sheets`)}
                      >
                        <ClipboardIcon />
                        {t('companies.ssmSheets')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs"
                        onClick={() => navigate(`/companies/${company.id}/documents`)}
                      >
                        <DocumentIcon />
                        {t('companies.documents')}
                      </Button>
                    </div>

                    {/* Dezactivare & Ștergere */}
                    {['SUPER_ADMIN', 'SSM_COMPANY_ADMIN'].includes(user?.role) && (
                      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                        <button
                          onClick={() => handleDeactivate(company)}
                          disabled={isAnyAction || !company.active}
                          title={
                            !company.active
                              ? t('companies.alreadyInactive')
                              : t('companies.deactivateKeepData')
                          }
                          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition
                          bg-warning-50 dark:bg-warning-950/50 text-warning-700 dark:text-warning-300 hover:bg-warning-100
                          disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {isDeactivating ? (
                            <svg
                              className="animate-spin w-3.5 h-3.5"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                              />
                            </svg>
                          ) : (
                            <PauseIcon />
                          )}
                          {t('companies.deactivate')}
                        </button>
                        <button
                          onClick={() => handleDelete(company)}
                          disabled={isAnyAction}
                          title={t('companies.deleteForeverTooltip')}
                          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition
                          bg-danger-50 dark:bg-danger-950/50 text-danger-700 dark:text-danger-300 hover:bg-danger-100
                          disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {isDeleting ? (
                            <svg
                              className="animate-spin w-3.5 h-3.5"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                              />
                            </svg>
                          ) : (
                            <TrashIcon />
                          )}
                          {t('companies.deleteAll')}
                        </button>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filtered.length}
            pageSize={PAGE_SIZE}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {/* Legendă acțiuni */}
      {['SUPER_ADMIN', 'SSM_COMPANY_ADMIN'].includes(user?.role) && (
        <Alert type="warning">
          <div className="text-xs">
            <p>
              <Trans i18nKey="companies.legendDeactivate" components={{ b: <strong /> }} />
            </p>
            <p className="mt-1">
              <Trans i18nKey="companies.legendDelete" components={{ b: <strong /> }} />
            </p>
          </div>
        </Alert>
      )}

      {/* Modal: Dezactivare firmă client */}
      <Modal
        open={!!deactivateTarget}
        onClose={() => {
          setDeactivateTarget(null);
          setActionError('');
        }}
        title={t('companies.deactivateTitle')}
        footer={
          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              onClick={() => setDeactivateTarget(null)}
              disabled={!!actionLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="warning"
              onClick={confirmDeactivate}
              loading={actionLoading?.endsWith('_deactivate')}
            >
              {t('companies.yesDeactivate')}
            </Button>
          </div>
        }
      >
        {deactivateTarget && (
          <div className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
            <p>
              <Trans
                i18nKey="companies.deactivateConfirmName"
                values={{ name: deactivateTarget.name }}
                components={{ b: <strong /> }}
              />
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('companies.deactivateDetails')}
            </p>
            {actionError && (
              <p className="text-xs text-danger-600 dark:text-danger-300">{actionError}</p>
            )}
          </div>
        )}
      </Modal>

      {/* Modal: Ștergere definitivă firmă client */}
      <Modal
        open={!!deleteTarget}
        onClose={() => {
          setDeleteTarget(null);
          setDeleteConfirmName('');
          setActionError('');
        }}
        title={t('companies.deleteTitle')}
        footer={
          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              onClick={() => setDeleteTarget(null)}
              disabled={!!actionLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
              loading={actionLoading?.endsWith('_delete')}
              disabled={!!actionLoading || deleteConfirmName !== deleteTarget?.name}
            >
              {t('companies.deleteForever')}
            </Button>
          </div>
        }
      >
        {deleteTarget && (
          <div className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
            <div className="bg-danger-50 dark:bg-danger-950/50 border border-danger-200 dark:border-danger-700 rounded-lg p-3">
              <p className="font-semibold text-danger-800 dark:text-danger-300 mb-1">
                {t('companies.deleteWarning')}
              </p>
              <p className="text-xs text-danger-700 dark:text-danger-300">
                <Trans
                  i18nKey="companies.deleteConfirmDetails"
                  values={{
                    name: deleteTarget.name,
                    count: deleteTarget._count?.employees || 0,
                  }}
                  components={{ b: <strong /> }}
                />
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                <Trans
                  i18nKey="companies.typeNamePrompt"
                  values={{ name: deleteTarget.name }}
                  components={{
                    code: <code className="font-mono text-danger-700 dark:text-danger-300" />,
                  }}
                />
              </p>
              <Input
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder={deleteTarget.name}
              />
            </div>
            {actionError && (
              <p className="text-xs text-danger-600 dark:text-danger-300">{actionError}</p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Companies;
